import { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, RotateCcw, Loader2, Clock, FileAudio, ChevronDown, Edit3, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

type VoiceMode = 'transcript' | 'casual' | 'formal' | 'warm_personal' | 'hot_take' | 'my_style';

interface UserAIPrefs {
  my_style_prompt?: string;
  default_voice_mode?: VoiceMode;
}

interface VoiceReviewProps {
  bookTitle?: string;
  bookAuthor?: string;
  onReviewGenerated: (review: string) => void;
  className?: string;
}

export function VoiceReview({ 
  bookTitle, 
  bookAuthor, 
  onReviewGenerated, 
  className = '' 
}: VoiceReviewProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [fileSize, setFileSize] = useState(0);
  const [selectedMode, setSelectedMode] = useState<VoiceMode>('casual');
  const [userPrefs, setUserPrefs] = useState<UserAIPrefs>({});
  const [isEditingMyStyle, setIsEditingMyStyle] = useState(false);
  const [myStyleInput, setMyStyleInput] = useState('');
  const [showTranscript, setShowTranscript] = useState(false);
  const [lastTranscript, setLastTranscript] = useState<string>('');
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioUrlRef = useRef<string | null>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const maxRecordingSeconds = 120;
  const minRecordingSeconds = 3;
  const maxFileSizeBytes = 25 * 1024 * 1024; // 25MB
  const allowedAudioTypes = ['audio/webm', 'audio/mp4', 'audio/m4a', 'audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/ogg'];
  
  const { toast } = useToast();

  // Load user preferences on mount
  useEffect(() => {
    loadUserPrefs();
  }, []);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);

  const loadUserPrefs = async () => {
    try {
      const { data, error } = await supabase
        .from('user_ai_prefs')
        .select('my_style_prompt, default_voice_mode')
        .single();
      
      if (error && error.code !== 'PGRST116') { // Not found is ok
        console.error('Error loading user prefs:', error);
        return;
      }
      
      if (data) {
        const prefs: UserAIPrefs = {
          my_style_prompt: data.my_style_prompt,
          default_voice_mode: data.default_voice_mode as VoiceMode
        };
        setUserPrefs(prefs);
        
        if (data.default_voice_mode && ['transcript', 'casual', 'formal', 'warm_personal', 'hot_take', 'my_style'].includes(data.default_voice_mode)) {
          setSelectedMode(data.default_voice_mode as VoiceMode);
        }
        if (data.my_style_prompt) {
          setMyStyleInput(data.my_style_prompt);
        }
      }
    } catch (err) {
      console.error('Error loading user preferences:', err);
    }
  };

  const saveMyStylePrompt = async (prompt: string) => {
    try {
      // Input validation for security
      const sanitizedPrompt = prompt.trim();
      if (sanitizedPrompt.length === 0) {
        toast({
          variant: "destructive",
          title: "Invalid input",
          description: "Style description cannot be empty.",
        });
        return;
      }
      
      if (sanitizedPrompt.length > 1000) {
        toast({
          variant: "destructive",
          title: "Input too long",
          description: "Style description must be 1000 characters or less.",
        });
        return;
      }
      
      // Check for potentially malicious content
      if (/<script|javascript:|data:|vbscript:/i.test(sanitizedPrompt)) {
        toast({
          variant: "destructive",
          title: "Invalid content",
          description: "Style description contains invalid content.",
        });
        return;
      }
      
      const { error } = await supabase
        .from('user_ai_prefs')
        .upsert({
          user_id: (await supabase.auth.getUser()).data.user?.id,
          my_style_prompt: sanitizedPrompt,
          updated_at: new Date().toISOString()
        });
      
      if (error) throw error;
      
      setUserPrefs(prev => ({ ...prev, my_style_prompt: sanitizedPrompt }));
      toast({
        title: "Style saved",
        description: "Your custom style has been saved successfully.",
      });
    } catch (error) {
      console.error('Error saving my style:', error);
      toast({
        variant: "destructive",
        title: "Save failed",
        description: "Could not save your custom style. Please try again.",
      });
    }
  };

  const setAsDefaultMode = async (mode: VoiceMode) => {
    try {
      const { error } = await supabase
        .from('user_ai_prefs')
        .upsert({
          user_id: (await supabase.auth.getUser()).data.user?.id,
          default_voice_mode: mode,
          updated_at: new Date().toISOString()
        });
      
      if (error) throw error;
      
      setUserPrefs(prev => ({ ...prev, default_voice_mode: mode }));
      toast({
        title: "Default set",
        description: `${getModeLabel(mode)} is now your default mode.`,
      });
    } catch (error) {
      console.error('Error setting default mode:', error);
      toast({
        variant: "destructive",
        title: "Failed to set default",
        description: "Could not set default mode. Please try again.",
      });
    }
  };

  const getModeLabel = (mode: VoiceMode): string => {
    switch (mode) {
      case 'transcript': return 'Transcript';
      case 'casual': return 'Casual';
      case 'formal': return 'Formal';
      case 'warm_personal': return 'Warm & Personal';
      case 'hot_take': return 'Hot-take';
      case 'my_style': return 'My Style';
      default: return 'Casual';
    }
  };

  const startRecording = async () => {
    try {
      setError(null);
      setRecordingDuration(0);
      
      // Check if mediaDevices is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Your browser does not support audio recording. Please use a modern browser like Chrome, Firefox, or Safari.');
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        } 
      });

      // Try different MIME types in order of preference
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
        'audio/mpeg',
        ''
      ];
      
      let selectedMimeType = '';
      for (const mimeType of mimeTypes) {
        if (mimeType === '' || MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          console.log('Using MIME type:', selectedMimeType || 'default');
          break;
        }
      }

      const options = selectedMimeType ? { mimeType: selectedMimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        audioUrlRef.current = URL.createObjectURL(audioBlob);
        setFileSize(audioBlob.size);
        setHasRecording(true);
        
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
        
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
      };

      // Start recording timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => {
          const newDuration = prev + 1;
          if (newDuration >= maxRecordingSeconds) {
            stopRecording();
            toast({
              title: "Recording auto-stopped",
              description: `Maximum recording duration (${maxRecordingSeconds}s) reached.`,
            });
          }
          return newDuration;
        });
      }, 1000);

      mediaRecorder.start(1000); // Collect data every 1000ms for better quality
      setIsRecording(true);
      
      toast({
        title: "Recording started",
        description: `Speak clearly about your thoughts on the book (${minRecordingSeconds}-${maxRecordingSeconds}s)...`,
      });
    } catch (err) {
      console.error('Error starting recording:', err);
      setError('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      
      const message = recordingDuration < minRecordingSeconds 
        ? `Recording is very short (${recordingDuration}s). For best results, try speaking for at least ${minRecordingSeconds} seconds.`
        : "Recording complete! You can now process it or record again.";
      
      toast({
        title: "Recording stopped",
        description: message,
        variant: recordingDuration < minRecordingSeconds ? "destructive" : "default"
      });
    }
  };

  const resetRecording = () => {
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setHasRecording(false);
    setError(null);
    setRecordingDuration(0);
    setFileSize(0);
    audioChunksRef.current = [];
  };

  const processRecording = async () => {
    if (!hasRecording || audioChunksRef.current.length === 0) {
      setError('No recording to process');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Authentication error - please sign in again');
      }

      // Check if My Style is selected but no prompt is available
      if (selectedMode === 'my_style' && !userPrefs.my_style_prompt && !myStyleInput.trim()) {
        setError('Please enter your style description first');
        setIsEditingMyStyle(true);
        return;
      }

      // Create FormData for legacy API support
      const formData = new FormData();
      formData.append('audio', audioBlob, 'voice-review.webm');
      formData.append('language', 'en');
      formData.append('mode', selectedMode);
      
      if (selectedMode === 'my_style' && myStyleInput.trim()) {
        formData.append('myStylePromptOverride', myStyleInput.trim());
      }

      // Call edge function
      const response = await fetch(`https://zjubnjpiajoscjmogyqb.supabase.co/functions/v1/reviews-transcribe-and-summarize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Edge function error:', data);
        
        if (data.code === 'MY_STYLE_NOT_SET') {
          setIsEditingMyStyle(true);
          setError('Please set up your custom style first');
          return;
        }
        
        if (data.code) {
          throw new Error(`${data.code}: ${data.message}`);
        }
        
        throw new Error(`Server error: ${response.status}`);
      }

      const { transcript, tidyReview, mode } = data;
      
      // Store transcript for viewing
      setLastTranscript(transcript || '');
      
      // Use tidyReview for all modes (including transcript mode where tidyReview = transcript)
      const reviewText = tidyReview || transcript;
      
      if (reviewText) {
        onReviewGenerated(reviewText);
        resetRecording();
        
        toast({
          title: mode === 'transcript' ? "Transcript ready!" : "Review generated!",
          description: mode === 'transcript' 
            ? "Your voice note has been transcribed."
            : "Your voice note has been transformed into a polished review.",
        });
      } else {
        throw new Error('No text was generated from your recording');
      }

    } catch (error) {
      console.error('Error processing recording:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to process recording';
      setError(errorMessage);
      
      toast({
        variant: "destructive",
        title: "Processing failed",
        description: errorMessage,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '';
    const kb = bytes / 1024;
    if (kb < 1024) return `${Math.round(kb)}KB`;
    return `${Math.round(kb / 1024 * 10) / 10}MB`;
  };

  return (
    <Card className={`${className}`}>
      <CardContent className="p-4 space-y-4">
        <div className="text-center">
          <h3 className="font-medium mb-2">Voice Review</h3>
          <p className="text-sm text-muted-foreground">
            Record your thoughts clearly for at least {minRecordingSeconds} seconds and we'll help turn them into a polished review
          </p>
        </div>

        <div className="flex items-start gap-2 px-3 py-2 bg-muted/50 rounded-md border border-border/50">
          <span className="text-lg flex-shrink-0 mt-0.5">🦖</span>
          <p className="text-xs text-muted-foreground">
            Remember: Dino's for honest, respectful reviews. Focus on the book, not the author.
          </p>
        </div>

        {/* Mode selector */}
        <div className="space-y-3">
          <div className="text-sm font-medium text-center">Review Style</div>
          <div className="grid grid-cols-3 gap-2">
            <Button
              type="button"
              variant={selectedMode === 'transcript' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedMode('transcript')}
              className="text-xs"
            >
              Transcript
            </Button>
            <Button
              type="button"
              variant={selectedMode === 'casual' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedMode('casual')}
              className="text-xs"
            >
              Casual
            </Button>
            <Button
              type="button"
              variant={selectedMode === 'formal' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedMode('formal')}
              className="text-xs"
            >
              Formal
            </Button>
            <Button
              type="button"
              variant={selectedMode === 'warm_personal' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedMode('warm_personal')}
              className="text-xs"
            >
              Warm & Personal
            </Button>
            <Button
              type="button"
              variant={selectedMode === 'hot_take' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedMode('hot_take')}
              className="text-xs"
            >
              Hot-take
            </Button>
            <Button
              type="button"
              variant={selectedMode === 'my_style' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedMode('my_style')}
              className="text-xs"
            >
              My Style
            </Button>
          </div>
          
          {/* Default mode selector */}
          {selectedMode !== userPrefs.default_voice_mode && (
            <div className="text-center">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setAsDefaultMode(selectedMode)}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Set as default
              </Button>
            </div>
          )}
        </div>

        {/* My Style editor */}
        {selectedMode === 'my_style' && (
          <div className="space-y-3 border rounded-lg p-3 bg-muted/30">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">My Style</span>
              {userPrefs.my_style_prompt && !isEditingMyStyle && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsEditingMyStyle(true);
                    setMyStyleInput(userPrefs.my_style_prompt || '');
                  }}
                >
                  <Edit3 className="w-3 h-3 mr-1" />
                  Edit
                </Button>
              )}
            </div>
            
            {isEditingMyStyle || !userPrefs.my_style_prompt ? (
              <div className="space-y-2">
                <Textarea
                  value={myStyleInput}
                  onChange={(e) => setMyStyleInput(e.target.value)}
                  placeholder="Describe your style... e.g., 'short, punchy, no spoilers, first-person'"
                  className="min-h-[80px] text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={async () => {
                      if (myStyleInput.trim()) {
                        await saveMyStylePrompt(myStyleInput.trim());
                        setIsEditingMyStyle(false);
                      }
                    }}
                    disabled={!myStyleInput.trim()}
                  >
                    <Save className="w-3 h-3 mr-1" />
                    Save & Use
                  </Button>
                  {userPrefs.my_style_prompt && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setIsEditingMyStyle(false);
                        setMyStyleInput(userPrefs.my_style_prompt || '');
                      }}
                    >
                      <X className="w-3 h-3 mr-1" />
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground bg-background p-2 rounded border">
                "{userPrefs.my_style_prompt}"
              </div>
            )}
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Recording info */}
        {(isRecording || hasRecording) && (
          <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
            {isRecording && (
              <div className="flex items-center gap-2 text-red-500">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <Clock className="w-4 h-4" />
                <span className="font-mono">{formatDuration(recordingDuration)}</span>
                <span className="text-xs">/ {maxRecordingSeconds}s</span>
              </div>
            )}
            {hasRecording && !isRecording && (
              <div className="flex items-center gap-2">
                <FileAudio className="w-4 h-4" />
                <span>{formatDuration(recordingDuration)} • {formatFileSize(fileSize)}</span>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-center gap-2">
          {!hasRecording ? (
            <>
              {!isRecording ? (
                <Button type="button" onClick={startRecording} className="flex items-center gap-2">
                  <Mic className="w-4 h-4" />
                  Start Recording
                </Button>
              ) : (
                <Button type="button" onClick={stopRecording} variant="destructive" className="flex items-center gap-2">
                  <MicOff className="w-4 h-4" />
                  Stop Recording
                </Button>
              )}
            </>
          ) : (
            <div className="flex flex-wrap justify-center gap-2">
              <Button type="button" onClick={resetRecording} variant="outline" size="sm">
                <RotateCcw className="w-4 h-4 mr-1" />
                Reset
              </Button>
              
              <Button 
                type="button"
                onClick={processRecording} 
                disabled={isProcessing || fileSize > 25 * 1024 * 1024 || recordingDuration < minRecordingSeconds || (selectedMode === 'my_style' && !userPrefs.my_style_prompt && !myStyleInput.trim())}
                className="flex items-center gap-1"
                title={
                  recordingDuration < minRecordingSeconds 
                    ? `Recording too short. Minimum ${minRecordingSeconds} seconds required.` 
                    : selectedMode === 'my_style' && !userPrefs.my_style_prompt && !myStyleInput.trim()
                    ? 'Please set up your custom style first'
                    : ''
                }
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  `Generate ${selectedMode === 'transcript' ? 'Transcript' : 'Review'}`
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Processing status */}
        {isProcessing && (
          <div className="text-center text-sm text-muted-foreground space-y-2">
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              Processing audio...
            </div>
            <p>
              {selectedMode === 'transcript' 
                ? 'Transcribing your voice note...' 
                : 'Transcribing and enhancing your review...'}
            </p>
            {import.meta.env.DEV && (
              <div className="text-xs bg-muted/50 p-2 rounded mt-2">
                <strong>Dev Info:</strong> Mode: {selectedMode}, Function: reviews-transcribe-and-summarize
              </div>
            )}
          </div>
        )}

        {/* Show transcript after processing */}
        {lastTranscript && selectedMode !== 'transcript' && (
          <Collapsible open={showTranscript} onOpenChange={setShowTranscript}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full justify-between">
                <span>View transcript</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showTranscript ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-2">
              <div className="text-xs bg-muted/50 p-3 rounded border">
                <div className="font-medium mb-1">Raw transcript:</div>
                <div className="text-muted-foreground">{lastTranscript}</div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* File size warning */}
        {hasRecording && fileSize > 25 * 1024 * 1024 && (
          <Alert variant="destructive">
            <AlertDescription>
              Recording is too large ({formatFileSize(fileSize)}). Maximum size is 25MB. Please record a shorter audio.
            </AlertDescription>
          </Alert>
        )}

        {/* Development diagnostics */}
        {import.meta.env.DEV && error && (
          <details className="text-xs bg-muted/30 p-2 rounded border-l-2 border-destructive">
            <summary className="cursor-pointer font-mono text-destructive mb-1">🔍 Error Details (Dev Only)</summary>
            <pre className="whitespace-pre-wrap text-xs overflow-auto max-h-32">
              {error}
            </pre>
            <p className="mt-2 font-mono">Function: reviews-transcribe-and-summarize</p>
          </details>
        )}
      </CardContent>
    </Card>
  );
}